import { urlSetPairs, urlScalarPairs, urlFlagPairs, requirementMet , dateRangesToParam, dateRangesFromParam, DATE_DIMS } from './deskFilters.js'
// src/lib/deskUrl.js — committed-search ⇄ URL hash (DESK_MODE_REWRITE_PLAN Phase 1).
// Pure serialize/parse over the ONE committed-search definition (deskSearchState.js).
//
// Shape: '#datadesk?q=ვაკე&type=Property&rooms=2,3&priceMax=200000' (plan §2.4 —
// readable params, never a base64 blob: humans, support, and agents hand-editing
// links all debug URLs; version tolerance comes from IGNORING unknown params).
//
// Invariant 1: every committed search has exactly ONE canonical URL — fixed param
// order, defaults omitted, arrays sorted (canonicalFilters does the sorting).
// Invariant 5: only SHAREABLE state serializes — image search (a local file) and
// draft box text (plan §2.5) never appear here.
// Georgian survives encodeURIComponent round-tripping AND copy-paste through chat
// apps (the utf8 transport trap) — test file covers a real ვაკე query.
//
// The deep-link listing path ('#datadesk/listing/<id>') is a SIBLING hash form,
// parsed here too (plan §2.6: one URL mechanism, not two) — it is an object link,
// not a search, so it round-trips separately from committed state.

import { makeCommitted, isEmptyCommitted, normStage } from './deskSearchState.js'

// Canonical param order (Invariant 1). Short, human names; filters map 1:1 onto
// canonicalFilters output. Unknown params from newer builds are ignored on parse.
const PARAM_ORDER = [
  // `agent` sorts beside `city` and `stage`: all three answer "which pile", not "which
  // property". It is admin/GM-only in the UI, but the URL layer stays role-blind — the gate
  // lives at the one place that turns committed state into a server predicate
  // (serverAdvFilters in IntakeTab), so a link carrying an agent scope opens as an ordinary
  // search for someone who may not set one, rather than half-applying.
  'q', 'type', 'stage', 'pool', 'city', 'agent', 'intent', 'kind', 'district',   // F-087: pool sorts beside stage — both answer "which pile"
  'rooms', 'bedrooms', 'priceMin', 'priceMax', 'currency',
  'bathMin', 'bathMax', 'floorMin', 'floorMax',
  'floorsTotalMin', 'floorsTotalMax', 'topFloor',
  'subtypes',   // B265 — sorts right after kind, which it refines
  // `restrFrom` sorts immediately after `restrict` because it is about those very entries
  // — a reader scanning the URL should meet the lenses and their provenance together.
  // `condition` sorts beside `features` — both are feature.code lists, and a reader scanning
  // the URL should meet them together even though they compose differently (OR vs AND).
  'areaMin', 'areaMax', 'landAreaMin', 'landAreaMax', 'restrict', 'restrFrom', 'condition', 'features', 'advanceMax', 'sources',
  'moveInBy', 'moveInHard',
  'dateField', 'dateFrom', 'dateTo',
  'claimsLt', 'claimsGt', 'seen',
  // 0294 — exposureMax/spread were never added here either; a key missing from this list
  // sorts to index -1 and lands FIRST, so the ordering (not the meaning) drifts silently.
  'exposureMax', 'keepMine', 'spread',
  'sellerType', 'ageMin', 'ageMax', 'ageKinds', 'viewsMin', 'promoted', 'footprint', 'engaged', 'reposts', 'ownerPlat',
  'photos', 'noDesc', 'missingFacts',
  'near', 'browse',
]

// ⚠️ REGISTRY FIRST, LITERAL SECOND — the literal wins any key declared in both, so this
// spread cannot change a param that exists today; it can only supply one for a registered
// filter nobody has hand-written here. That is the difference between the registry being a
// mechanism and being documentation with a test in front of it.
const F2P = { ...Object.fromEntries(urlScalarPairs()),   // committed-filters key → url param (scalars copied verbatim)
  priceMin: 'priceMin', priceMax: 'priceMax', currency: 'currency',
  bathroomsMin: 'bathMin', bathroomsMax: 'bathMax',
  balconyCountMin: 'balcMin', balconyCountMax: 'balcMax',   // 0757
  floorMin: 'floorMin', floorMax: 'floorMax',
  floorsTotalMin: 'floorsTotalMin', floorsTotalMax: 'floorsTotalMax',
  areaMin: 'areaMin', areaMax: 'areaMax',
  landAreaMin: 'landAreaMin', landAreaMax: 'landAreaMax',
  advanceMaxMonths: 'advanceMax',
  moveInBy: 'moveInBy',   // 0220 step 4 — a YYYY-MM-DD date, copied verbatim like a scalar
  // 0249 — all scalars, same verbatim copy. claimsLt/claimsGt are numbers whose ZERO is
  // meaningful ("none assigned"), and `put` guards on `!= null && !== ''`, not falsiness,
  // so 0 survives the round trip. Do not "simplify" that guard.
  dateField: 'dateField', dateFrom: 'dateFrom', dateTo: 'dateTo',
  claimsLt: 'claimsLt', claimsGt: 'claimsGt', seen: 'seen',
  // B215 (0262). '0' is a real threshold ("untouched only") — the same zero-survives
  // guard the claims note above insists on covers it.
  exposureMax: 'exposureMax',
  // B213 (0263). Band token, never numeric — no zero-guard subtleties here.
  spread: 'spread',
  // 0294 (v22), the hunter lenses. sellerType/promoted/ownerEngaged are tokens; the two
  // age bounds are numbers whose ZERO is meaningful ("posted today"), and viewsMin's zero
  // is meaningful too ("no views yet") — the `put` guard is `!= null && !== ''`, not
  // falsiness, so they survive. Same warning as the claims note above: do not simplify it.
  // ageKinds and footprint are arrays and are handled with the other lists below.
  sellerType: 'sellerType',
  ageMinDays: 'ageMin', ageMaxDays: 'ageMax',
  viewsMin: 'viewsMin', promoted: 'promoted', ownerEngaged: 'engaged',
  // 0303 band tokens; '0' is a token string so the falsiness caveat above does not bite.
  reposts: 'reposts', ownerPlatforms: 'ownerPlat',
  // Advert quality (v55). ONLY photos belongs here - F2P is scalars-copied-verbatim, and
  // noDescription/missingFacts are booleans, which get explicit put/get lines below the
  // way topFloor and keepMine do. Listing a boolean here would serialize it as "false".
  photos: 'photos',
}
const P2F = Object.fromEntries(Object.entries(F2P).map(([f, p]) => [p, f]))

// The WRITE half of the '#datadesk/listing/<id>' form parseHash reads below — moved here
// from MatchSuggestions.jsx (2026-07-25) so the two halves of one URL shape live together,
// and so components can build a listing link without importing the match cluster (which
// imports back out of CooperationChips — a cycle the share menu would otherwise close).
// Built from window.location.origin so it is correct on any deployment (no hardcoded host).
// The URL is never trusted on open: the server still enforces visibility when the
// recipient's Desk loads the listing, so a forwarded link to something they may not see
// lands on the normal not-available state.
export function listingDeepLink(listingId) {
  return `${window.location.origin}/#datadesk/listing/${encodeURIComponent(listingId)}`
}

// ── OPEN CARDS IN THE URL (2026-08-05) ────────────────────────────────────────────────
// Owner-reported: open a property (or a lead, or the reshare editor inside one), press
// refresh — and everything is gone, back to the feed. Agents refresh on purpose: the
// repost tab talks to the extension, so "reload after the extension updates" is a normal
// move mid-task, and it cost them the whole trail.
//
// So the card stack serializes too, as ONE extra param on the same hash the search
// already owns: '#datadesk?q=ვაკე&cards=L~<listingId>,C~<reqId>&view=share'. The search
// half is untouched — cards are APPENDED after serializeSearch has decided the canonical
// search form, so the browse=1 rule (which asks "is anything else set?") keeps its old
// answer and a card never makes an empty search look non-empty.
//
// `view` belongs to the LAST card only — the one on top, the only one a click can reach.
// A lower card keeps its own open editor in React state (every stack entry stays mounted);
// the URL only has to be able to rebuild the trail, and rebuilding it opens each lower
// card in its default view. That is the deliberate limit of this restore.
const CARD_PREFIX = { listing: 'L', client: 'C' }
const CARD_KIND = { L: 'listing', C: 'client' }
// The deal editor's three tabs (DealEditor.jsx). An unknown token is DROPPED rather than
// passed through: a stale link from an older build must open the card, not a blank editor.
const CARD_VIEWS = ['coop', 'terms', 'share']

export function serializeCards(cards) {
  return (cards || []).map(c => {
    const p = CARD_PREFIX[c && c.kind]
    const id = c && (c.kind === 'client' ? c.reqId : c.id)
    return p && id ? `${p}~${id}` : null
  }).filter(Boolean).join(',')
}

export function parseCards(raw, view) {
  const out = (raw || '').split(',').map(s => s.trim()).filter(Boolean).map(tok => {
    const m = tok.match(/^([LC])~([0-9a-f-]{8,})$/i)
    if (!m) return null
    const kind = CARD_KIND[m[1].toUpperCase()]
    // isOwn:false is the same initial hint the deep-link path has always passed — the
    // modal reflects real ownership from its own payload, so the URL never carries it.
    return kind === 'client' ? { kind, reqId: m[2] } : { kind, id: m[2], isOwn: false }
  }).filter(Boolean).slice(-8)   // same runaway cap as the live stack (IntakeTab)
  // ⚠️ Only a LISTING card has a deal editor. A `view` riding on top of a client card is a
  // statement the URL cannot honour — the client modal ignores it, so it would sit in the
  // bar forever describing something that is not open. Drop it at the door.
  const v = CARD_VIEWS.includes(view) ? view : null
  const top = out.length ? out[out.length - 1] : null
  if (v && top && top.kind === 'listing') top.view = v
  return out
}

// Append the card trail to an already-canonical search hash. Kept separate from
// serializeSearch (rather than folded into it) because the two answer different
// questions — what is being searched vs what is open on top of it — and only the
// first one is what a saved search or a shared search link may carry.
// The page is VIEW state, not search state, so it is appended after serializeSearch the same
// way the card trail is — a page number must never make an empty search look non-empty.
//
// ⚠️ ONE-BASED IN THE URL, zero-based in state. The bar is read by people; "page=3" has to mean
// the third page. And page 1 is OMITTED: it is the default, so every ordinary search keeps a
// clean URL and a link with no `page` is unambiguous rather than "page 1 or unstated?".
export function withPage(hash, page) {
  const n = Math.floor(Number(page) || 0)
  if (!(n > 0)) return hash
  const sep = hash.includes('?') ? '&' : '?'
  return `${hash}${sep}page=${n + 1}`
}

// The ORDER the page belongs to (audit 2026-10-29 D3). View state like the page, appended the same way, and only
// meaningful beside it: "page 3" of Cheapest and "page 3" of Activity are different records, and both used to write
// the identical '#datadesk?type=Property&page=3' — a reload restored Activity while keeping page 3.
// ⚠️ 'activity' is the default and is OMITTED, so every link written before this keeps meaning what it meant.
// The value is not validated here — this module does not own the sort menu. The Desk checks it on restore.
export const DEFAULT_SORT = 'activity'
// ⚠️ The default is PER PILE (audit 2026-10-29 FD-1): Leads opens on Best Match, every other pile on Activity. With one
// global default, a deliberately picked Leads "Activity" was omitted as "the default", and the reload read the gap as
// Leads' default — Best Match — while keeping page 3 of an Activity list. Omit only the pile's OWN default, so Leads
// Activity is written (`sort=activity`) and an absent Leads sort still means Best Match (every older Leads link).
export const defaultSortFor = stage => (stage === 'leads' ? 'best_match' : DEFAULT_SORT)
export function withSort(hash, sort, dflt = DEFAULT_SORT) {
  if (!sort || sort === dflt) return hash
  const sep = hash.includes('?') ? '&' : '?'
  return `${hash}${sep}sort=${encodeURIComponent(sort)}`
}

export function withCards(hash, cards, view) {
  const c = serializeCards(cards)
  if (!c) return hash
  const sep = hash.includes('?') ? '&' : '?'
  const top = cards[cards.length - 1]
  const v = CARD_VIEWS.includes(view) && top && top.kind === 'listing' ? `&view=${view}` : ''
  return `${hash}${sep}cards=${encodeURIComponent(c)}${v}`
}

export function serializeSearch(committed) {
  const c = makeCommitted(committed || {})
  if (isEmptyCommitted(c)) return '#datadesk'
  const f = c.filters
  const kv = []
  const put = (k, v) => { if (v != null && v !== '') kv.push([k, String(v)]) }
  put('q', f.q)
  put('type', c.type)
  // Stage (§3b) is ALWAYS written once stated — deliberately unlike other defaults. If the
  // landing default were omitted, a URL with no `stage` would be ambiguous between "leads"
  // and "predates the stage axis", and the legacy reading is the one that must win. Writing
  // it explicitly keeps Invariant 1 (the form is still deterministic: one search, one URL).
  put('stage', c.stage)
  put('pool', c.pool)   // F-087: 'archived' only; active is the landing default and is never written
  put('city', c.city)
  // 2026-08-09 — the admin's agent filter. It reached the server from the day it shipped
  // (2026-07-28) but lived outside advFilters, so it was in NO other layer: no chip, no
  // active-filter signal, and no clear button reset it. Promoting it to a committed filter
  // is what makes every one of those fall out of the rules that already exist, rather than
  // being four more hand-written special cases.
  put('agent', (f.agentIds || []).join(','))
  put('intent', (f.intents || []).join(','))
  put('kind', (f.kinds || []).join(','))
  put('district', (f.districtCodes || []).join(','))
  put('district_own', (f.districtOwnCodes || []).join(','))
  // 0537/0538: street UUIDs. They travel like district codes because a street search is
  // now a real selection, not a proximity guess — a shared link to "Politkovskaya" must
  // reopen on Politkovskaya, not on whatever is within 700m of its centre.
  put('streets', (f.streetIds || []).join(','))
  put('complexes', (f.complexIds || []).join(','))
  // 0688: metro station UUIDs travel like the street ones, for the same reason — a link
  // shared as "near ვარკეთილი" must reopen on that station, not on a radius around it.
  put('metro', (f.metroIds || []).join(','))
  // Q4: the nearby opt-in travels too, because it changes what the page ANSWERS, not just
  // how it looks. A shared link that silently dropped it would reopen showing fewer results
  // than the person who sent it saw, with nothing on screen to explain the difference.
  if (f.streetNearbyM) put('streets_near', String(f.streetNearbyM))
  // B1188: the four date ranges, written as the compact codec form rather than JSON -- a URL
  // is read and shared by people, and JSON in a hash is percent-encoded noise. Hand-written
  // because the value is an object; the registry marks it urlCustom so F2P leaves it alone.
  put('dates', dateRangesToParam(f.dateRanges))
  put('rooms', (f.roomsSet || []).join(','))
  put('bedrooms', (f.bedroomsSet || []).join(','))
  // ⚠️ requirementMet: a MODIFIER is not written when the thing it modifies is absent
  // (currency without a price bound, dateField without a date range). Keys with no declared
  // requirement always pass, so every pre-existing filter is unaffected.
  // ⚠️ The kv guard is the same one the flag and set loops carry, and for the same reason: a
  // hand-written put() above may already have emitted this param (streets_near does), and a
  // duplicate round-trips correctly, so only reading the URL reveals it.
  for (const [fk, pk] of Object.entries(F2P))
    if (requirementMet(f, fk) && !kv.some(([k]) => k === pk)) put(pk, f[fk])
  if (f.topFloor) put('topFloor', '1')
  if (f.moveInHard) put('moveInHard', '1')
  if (f.keepMine) put('keepMine', '1')
  if (f.noDescription) put('noDesc', '1')
  if (f.missingFacts) put('missingFacts', '1')
  // Registered FLAGS: '1' when true, absent when not.
  // ⚠️ MUST RUN AFTER the hand-written lines above, not before, so its guard can see what they
  // already emitted -- the set loop below is placed the same way for the same reason. Written
  // before them first, and the URL came back `topFloor=1&topFloor=1`: a duplicate that parses
  // correctly and is therefore invisible to a round-trip test. Caught by printing the URL.
  for (const [fk, pk] of urlFlagPairs()) { if (f[fk] && requirementMet(f, fk) && !kv.some(([k]) => k === pk)) put(pk, '1') }
  put('restrict', (f.restrictions || []).join(','))   // B177 '<code>:<hide|allow>' entries
  // The provenance marker (turn 5 / 6b): `<clientId>~<code:mode>,<code:mode>`. It travels
  // because a shared link or a restored refresh must be able to say "these lenses are
  // Nino's" — a search that forgets whose answers it is carrying degrades into one that
  // presents a derivation as the agent's own choice.
  // `~` separates the two halves because the entries themselves contain `:` and are
  // comma-joined; a delimiter already in use would make the form ambiguous to parse.
  // ⚠️ It is redundant with `restrict` ON PURPOSE — makeCommitted prunes it against the
  // real restrictions on the way back in, so a stale or hand-edited marker cannot claim a
  // lens the search does not carry. Provenance has to name the value it vouches for.
  if (c.restrictionsFrom) put('restrFrom', `${c.restrictionsFrom.clientId}~${c.restrictionsFrom.derived.join(',')}`)
  put('features', (f.featureCodes || []).join(','))   // B37 bare feature.code entries
  // 0756 — the four-state feature filter. THIS IS THE SITE THAT WAS MISSED, and the live Desk
  // caught it: the control filtered correctly and the URL stayed at ?kind=apartment, so a
  // refresh or a shared link silently dropped the question while the chip still claimed it.
  // Fourth recorded instance of the half-existing-key trap, and the fourth site of it.
  put('feature_states', (f.featureStates || []).join(','))
  put('condition', (f.conditionCodes || []).join(',')) // 0433 — the რემონტი/კარკასი rungs
  // 0231 (found saving a leads search): the portal filter was in NEITHER the committed
  // keys NOR here — the half-existing-key trap AGAIN (see COMMITTED_FILTER_KEYS note).
  // Without it a saved SS.ge leads search silently restored as an unfiltered one.
  put('sources', (f.sources || []).join(','))
  // B265 — the subtype tokens. Same commit adds it to COMMITTED_FILTER_KEYS, the deskFeed
  // gate, SERVER_ONLY_ADV_KEYS and the transport, per the half-existing-key rule above.
  put('subtypes', (f.subtypes || []).join(','))
  // 0294 — the two array-valued hunter lenses. ageKinds rides along even though it is a
  // modifier rather than a constraint, for the same reason dateField does: without it the
  // restored search measures age by a different definition than the one that was shared.
  put('ageKinds', (f.ageKinds || []).join(','))
  put('footprint', (f.footprint || []).join(','))
  if (f.near) put('near', `${f.near.lat},${f.near.lng},${f.near.radiusM}${f.near.id ? ',' + f.near.id : ''}`)
  // ── REGISTRY-DRIVEN SETS ────────────────────────────────────────────────────────────
  // Every registered 'set' filter travels as one comma-joined param, exactly like the
  // hand-written ones above.
  // ⚠️ IT RUNS LAST, AND THAT POSITION IS THE WHOLE CORRECTNESS ARGUMENT. The skip below can
  // only see params already in `kv`, so placed earlier it could not see the hand-written
  // `put('features', …)` / `put('condition', …)` / `put('feature_states', …)` that come after
  // it — and it emitted all three TWICE. The round-trip still passed (parse takes one of the
  // pair), which is exactly why this had to be caught by reading the URL rather than by the
  // test: a duplicated param is a correct-looking answer with a doubled query string.
  // ⚠️ It must also run BEFORE the browse=1 guard: that guard asks whether anything at all was
  // written, so a filter emitted after it would produce `browse=1` alongside a real filter —
  // two URLs for one search, which is Invariant 1.
  for (const [fk, pk] of urlSetPairs()) {
    if (kv.some(([k]) => k === pk)) continue
    if (!requirementMet(f, fk)) continue
    put(pk, (f[fk] || []).join(','))
  }
  // browse=1 only when NOTHING else marks the state non-empty — a q/type/filter
  // already implies the feed, and emitting it anyway would violate Invariant 1
  // (two URLs for one search).
  if (!kv.length) put('browse', '1')
  kv.sort((a, b) => PARAM_ORDER.indexOf(a[0]) - PARAM_ORDER.indexOf(b[0]))
  return '#datadesk?' + kv.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')
}

// parse(hash) → { kind: 'home' | 'search' | 'listing' | 'share', committed?, listingId?, cards }.
// `cards` is present on every kind except 'share' (see below) — it is orthogonal to what
// the search half says, so the caller restores it first and then applies the rest.
// Tolerant: unknown params ignored; malformed values dropped field-by-field
// (agents paste links across deploy boundaries — never throw).
export function parseHash(hash) {
  const h = (hash || '').replace(/^#/, '')
  if (!h.startsWith('datadesk')) return null   // not ours (other tabs own their hashes)
  const rest = h.slice('datadesk'.length)
  // The externally-shared object link (listingDeepLink above). It carries no search and no
  // trail, so it restores as a one-card stack — the same thing a click produces, which is
  // what makes the writer able to canonicalize it into the '?cards=' form on arrival.
  const mListing = rest.match(/^\/listing\/([0-9a-f-]{8,})$/i)
  if (mListing) return { kind: 'listing', listingId: mListing[1], cards: [{ kind: 'listing', id: mListing[1], isOwn: false }] }
  // 🔗 Arriving from the client page's logo (2026-08-04). The token is resolved
  // server-side and the hash is stripped immediately — see applyParsedHash.
  //
  // ⚠️ NEVER serialize this back out. Every other kind here round-trips through
  // serializeSearch, but a share token is a bearer credential (0280): it may be
  // read from the URL once, on arrival, and must not be written back into the bar,
  // pushState history, or a saved search.
  const mShare = rest.match(/^\/share\/([a-f0-9]{32})$/i)
  if (mShare) return { kind: 'share', shareToken: mShare[1].toLowerCase() }
  if (!rest.startsWith('?')) return { kind: 'home', committed: makeCommitted({}), cards: [], page: 0, sort: DEFAULT_SORT }
  const sp = new URLSearchParams(rest.slice(1))
  const cards = parseCards(sp.get('cards'), sp.get('view'))
  // ⚠️ Clamped at the low end only. A negative or nonsense page is 0; a page PAST the end is
  // left alone here because this module cannot know how many pages exist — the feed clamps it
  // when it knows, which is the only place that can.
  const pageRaw = parseInt(sp.get('page'), 10)
  const page = Number.isFinite(pageRaw) && pageRaw > 1 ? pageRaw - 1 : 0
  const list = v => (v || '').split(',').map(s => s.trim()).filter(Boolean)
  const filters = {}
  if (sp.get('q')) filters.q = sp.get('q')
  if (sp.get('intent')) filters.intents = list(sp.get('intent'))
  if (sp.get('kind')) filters.kinds = list(sp.get('kind'))
  if (sp.get('district')) filters.districtCodes = list(sp.get('district'))
  if (sp.get('district_own')) filters.districtOwnCodes = list(sp.get('district_own'))
  if (sp.get('streets')) filters.streetIds = list(sp.get('streets'))
  if (sp.get('complexes')) filters.complexIds = list(sp.get('complexes'))
  if (sp.get('metro')) filters.metroIds = list(sp.get('metro'))
  if (sp.get('streets_near')) filters.streetNearbyM = parseInt(sp.get('streets_near'), 10) || 0
  if (sp.get('agent')) filters.agentIds = list(sp.get('agent'))
  if (sp.get('sources')) filters.sources = list(sp.get('sources'))
  if (sp.get('subtypes')) filters.subtypes = list(sp.get('subtypes'))   // B265
  if (sp.get('rooms')) filters.roomsSet = list(sp.get('rooms')).map(Number).filter(Number.isFinite)
  if (sp.get('bedrooms')) filters.bedroomsSet = list(sp.get('bedrooms')).map(Number).filter(Number.isFinite)
  // The read half of the loop above. ⚠️ It must stay a MIRROR of it: a param written by one
  // and not read by the other is a filter that survives the share and dies on the open — the
  // half-existing-key trap wearing its URL costume.
  for (const [fk, pk] of urlSetPairs()) {
    if (filters[fk] !== undefined) continue
    if (sp.get(pk)) filters[fk] = list(sp.get(pk))
  }
  for (const [pk, fk] of Object.entries(P2F)) if (sp.get(pk) != null && sp.get(pk) !== '') filters[fk] = sp.get(pk)
  // B1188. Read AFTER the scalar loop so a malformed value cannot be left half-applied by it,
  // and BEFORE the legacy migration below, which must not overwrite an explicit new-form value.
  if (sp.get('dates')) {
    const dr = dateRangesFromParam(sp.get('dates'))
    if (Object.keys(dr).length) filters.dateRanges = dr
  }
  // ⚠️ MIGRATION, and it is not optional: every URL and saved search minted before today
  // carries dateField/dateFrom/dateTo, and the transport now suppresses that trio whenever
  // dateRanges is set. Without this, an old shared link would keep working only by accident,
  // and the panel -- which reads dateRanges -- would show an empty date section over a search
  // that was in fact date-filtered. Read one way: the trio becomes the matching dimension.
  if (!filters.dateRanges && (sp.get('dateFrom') || sp.get('dateTo'))) {
    const dim = sp.get('dateField') || 'added'
    if (DATE_DIMS.includes(dim)) {
      filters.dateRanges = { [dim]: { from: sp.get('dateFrom') || '', to: sp.get('dateTo') || '' } }
    }
  }
  // 2026-10-22: …and the trio is DROPPED once dateRanges holds the range. It used to be left in
  // place; clearing the dateRanges chip then let the trio (suppressed only while dateRanges is set)
  // back onto the wire — a date filter no control could remove.
  if (filters.dateRanges) { delete filters.dateField; delete filters.dateFrom; delete filters.dateTo }
  for (const [fk, pk] of urlFlagPairs()) { if (sp.get(pk) === '1') filters[fk] = true }
  if (sp.get('topFloor') === '1') filters.topFloor = true
  if (sp.get('moveInHard') === '1') filters.moveInHard = true
  if (sp.get('keepMine') === '1') filters.keepMine = true
  if (sp.get('noDesc') === '1') filters.noDescription = true
  if (sp.get('missingFacts') === '1') filters.missingFacts = true
  if (sp.get('restrict')) filters.restrictions = list(sp.get('restrict'))
  if (sp.get('features')) filters.featureCodes = list(sp.get('features'))
  if (sp.get('feature_states')) filters.featureStates = list(sp.get('feature_states'))   // 0756
  if (sp.get('condition')) filters.conditionCodes = list(sp.get('condition'))   // 0433
  if (sp.get('ageKinds')) filters.ageKinds = list(sp.get('ageKinds'))       // 0294
  if (sp.get('footprint')) filters.footprint = list(sp.get('footprint'))     // 0294
  if (sp.get('near')) {
    const [lat, lng, radiusM, id] = sp.get('near').split(',')
    // ⚠️ The label is NOT carried and cannot be: this is a comma-split field and a place name
    // may contain a comma. The id is here, so IntakeTab's committed-near chip looks the name
    // up in the gazetteer instead of showing a bare `📍 ~300m` (B762b). Derive, never
    // serialize a second copy of something the id already answers.
    if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)))
      filters.near = { id: id || null, lat: Number(lat), lng: Number(lng), radiusM: Number(radiusM) || 1000 }
  }
  // ⚠️ LEGACY MIGRATION (§3b, 2026-08-02) — `type=Leads` was how the stage was carried while
  // Leads pretended to be an Object. Every saved search in the system predates the ruling
  // (measured: 5 of 5 are `type=Leads`), and this parser is DELIBERATELY TOLERANT — unknown
  // params are ignored — so an unmapped `type=Leads` would silently restore as a search over
  // the wrong inventory rather than fail. Map it explicitly; never delete this without first
  // rewriting `agent_saved_search.params`. (0231 already shipped this exact bug once, when a
  // saved SS.ge leads search came back unfiltered.)
  const rawType = sp.get('type') || ''
  const legacyLeads = rawType === 'Leads'
  // Provenance marker. Malformed values are DROPPED field-by-field like everything else
  // here — a link from an older build, or one an agent hand-edited, must still open the
  // search it describes; losing the attribution is a smaller harm than refusing the search.
  // Anything that survives is then pruned against the real restrictions by makeCommitted.
  let restrictionsFrom = null
  const rawFrom = sp.get('restrFrom')
  if (rawFrom) {
    const cut = rawFrom.indexOf('~')
    const clientId = cut < 0 ? '' : rawFrom.slice(0, cut)
    const derived = cut < 0 ? [] : list(rawFrom.slice(cut + 1))
    if (clientId && derived.length) restrictionsFrom = { clientId, derived }
  }
  const committed = makeCommitted({
    browse: sp.get('browse') === '1' || legacyLeads,
    type: legacyLeads ? '' : rawType,
    stage: legacyLeads ? 'leads' : normStage(sp.get('stage')),
    pool: sp.get('pool') === 'archived' ? 'archived' : null,   // F-087
    city: sp.get('city') || '',
    filters,
    restrictionsFrom,
  })
  // ⚠️ Cards do NOT make a hash a 'search'. A card opened over the home feed serializes as
  // '#datadesk?cards=…', whose search half is still empty — reporting that as a search would
  // strand the agent in browse mode behind the card when they close it.
  return { kind: isEmptyCommitted(committed) ? 'home' : 'search', committed, cards, page, sort: sp.get('sort') || defaultSortFor(committed.stage) }
}
